import { useTranslation } from "react-i18next";

import { useAuth } from "@/Components/Auth/AuthProvider";
import { useMyGroupsQuery } from "@/Components/Groups/Queries";
import type { UserGroup } from "@/types/user";

export function useUserGroups() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { data: groups, isLoading, error } = useMyGroupsQuery();

	const getLoggedGroup = (): UserGroup | null => {
		if (!user?.user_uuid || !groups) return null;
		return groups.find((group) => group.uuid === user.sub) || null;
	};

	const getGroupName = (uuid?: string): string => {
		if (!uuid || !groups) return t("Personal");
		return (
			groups.find((group) => group.uuid === uuid)?.name || t("Personal")
		);
	};

	const getLoggedGroupName = (): string => {
		return getGroupName(getLoggedGroup()?.uuid);
	};

	const getLoggedGroupIsPublic = (): boolean => {
		return getLoggedGroup()?.isPublic ?? false;
	};

	const isGroupOwner = (groupUuid: string): boolean => {
		if (!user || !groups) return false;
		const group = groups.find((g) => g.uuid === groupUuid);
		if (!group || !group.owners) return false;
		const userUuid = user.user_uuid || user.sub;
		return group.owners.some((owner) => owner.uuid === userUuid);
	};

	const ownedGroupsCount = (): number => {
		if (!groups || !user) return 0;
		const userUuid = user.user_uuid || user.uuid || user.sub;
		return groups.filter((group) =>
			group.owners?.some((owner) => owner.uuid === userUuid)
		).length;
	};

	const privateGroups = (): UserGroup[] => {
		return groups?.filter((g) => !g.isPublic) || [];
	};

	return {
		groups,
		isLoading,
		error,
		getLoggedGroup,
		getGroupName,
		getLoggedGroupName,
		getLoggedGroupIsPublic,
		isGroupOwner,
		ownedGroupsCount,
		privateGroups,
	};
}
